'use client';

import { useTranslations } from 'next-intl';
import { Maximize2 } from 'lucide-react';
import { SettingsItem } from '@/shared/ui/settings';
import { ProBadge } from '@/features/subscription';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';

interface HiResSectionProps {
  isHires: boolean;
  onIsHiresChange: (value: boolean) => void;
  /** Advanced engine control — locked (Pro Pass upsell) when the building lacks
   *  `has_advanced_render_controls`. Locked → the row shows the PRO badge and a
   *  tap opens the Pro Pass paywall instead of toggling. Mirrors Apple/Android. */
  locked?: boolean;
}

/**
 * Hi-Res rendering toggle — an advanced engine control. Always visible; when the
 * building lacks `has_advanced_render_controls` it renders LOCKED with a PRO
 * badge and a tap opens the Pro Pass paywall (no toggle). Unlocked (Pro Pass /
 * unlimited building) it's an interactive switch. Mirrors the iOS Engine row.
 */
export function HiResSection({
  isHires,
  onIsHiresChange,
  locked = false,
}: HiResSectionProps) {
  const t = useTranslations('render.settings.hires');
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const showPackPaywall = useLimitPaywallStore((s) => s.showPackPaywall);
  // Server-driven "unlock Hi-Res" payload (the Facade Kit unlocks it) — see
  // ModelContent.tsx for the full rationale.
  const hiresPaywall = usePaywallPayload('PRO_FEATURE_UNAVAILABLE');

  if (locked) {
    return (
      <SettingsItem
        icon={Maximize2}
        title={t('title')}
        paddingClassName="px-4 py-3"
        titleAdornment={<ProBadge />}
        onClick={() => {
          // Until the cached plan payload has loaded (rare — the plan is
          // fetched well before the user reaches this control) open the Kit
          // sheet directly; it loads its own copy from GET /store. Parity with
          // Apple's `showFeaturePaywall` and Android's `FeaturePaywallViewModel`
          // fallback — a locked knob must never be a dead tap.
          if (hiresPaywall) {
            showLimitPaywall(hiresPaywall, 'PRO_FEATURE_UNAVAILABLE');
          } else {
            showPackPaywall();
          }
        }}
      />
    );
  }

  return (
    <SettingsItem
      icon={Maximize2}
      title={t('title')}
      paddingClassName="px-4 py-3"
      toggle={{
        checked: isHires,
        onCheckedChange: onIsHiresChange,
      }}
      rowTogglesSwitch
    />
  );
}
